import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff, Loader2, Leaf } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { api, openWhatsApp } from "@/lib/api";
import { getAuthRedirectUrl } from "@/lib/native";
import { useApp } from "@/context/AppContext";

export default function Auth() {
  const navigate = useNavigate();
  const { setUser, continueAsGuest } = useApp();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (mode === "register" && !form.name)) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const res = await api.post(endpoint, payload);
      setUser(res.data.user);
      toast.success(mode === "register" ? "Welcome aboard!" : "Welcome back!");
      navigate("/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // On web this is window.location.origin + "/home"; only the native Capacitor shell uses the production domain bounce page.
    const redirectUrl = getAuthRedirectUrl();
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgot = () => {
    toast("Password help", { description: "Our team will help you reset it over WhatsApp." });
    openWhatsApp(`Hello U&URS NUTRITIONIST, I forgot my app password${form.email ? ` for ${form.email}` : ""}. Please help me reset it.`);
  };

  const handleGuest = () => {
    continueAsGuest();
    toast("Exploring as guest", { description: "Some features need an account." });
    navigate("/home");
  };

  const isLogin = mode === "login";

  return (
    <div className="h-full flex flex-col bg-[#fbf9f2] dark:bg-emerald-950 overflow-y-auto" data-testid="auth-screen">
      <div className="relative px-7 pt-10 pb-6 overflow-hidden" style={{ paddingTop: "max(env(safe-area-inset-top), 2.5rem)" }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#dbe7d0] dark:bg-emerald-800/40 blur-3xl" />
        <Leaf className="absolute top-6 right-6 w-8 h-8 text-emerald-800/10 dark:text-emerald-300/10 rotate-45" />
        <Leaf className="absolute top-24 left-4 w-5 h-5 text-emerald-800/10 dark:text-emerald-300/10 -rotate-12" />
        <div className="relative flex flex-col items-center text-center animate-fade-up">
          <BrandMark size={72} className="ring-1 ring-[#e3d9b8]/70" />
          <p className="mt-3 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#a6823a] dark:text-amber-300">U&URS NUTRITIONIST</p>
          <h1 className="mt-3 text-2xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            {isLogin ? "Continue your personalized wellness journey" : "Start your personalized nutrition path"}
          </p>
        </div>
      </div>

      <div className="px-5 pb-8 flex-1 flex flex-col">
        <div className="rounded-3xl bg-white dark:bg-emerald-900/60 border border-[#ece6d2] dark:border-emerald-800 shadow-[0_10px_40px_-15px_rgba(6,78,59,0.25)] p-5 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <button
            data-testid="login-google-btn"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white dark:bg-emerald-950 border border-slate-200 dark:border-emerald-800 text-slate-700 dark:text-slate-100 font-semibold py-3.5 rounded-2xl transition-colors hover:bg-slate-50 dark:hover:bg-emerald-800"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200 dark:bg-emerald-800" />
            <span className="text-[11px] font-semibold tracking-wider text-slate-400">OR</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-emerald-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            {!isLogin && (
              <Field label="Full Name" icon={User} testid="auth-name" placeholder="Enter your full name" value={form.name} onChange={update("name")} />
            )}
            <Field label="Email Address" icon={Mail} testid="auth-email" type="email" inputMode="email" autoComplete="email" placeholder="Enter your email" value={form.email} onChange={update("email")} />
            <div>
              <span className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ml-1">Password</span>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  data-testid="auth-password"
                  type={showPass ? "text" : "password"}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={update("password")}
                  className="w-full pl-11 pr-12 py-3.5 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition"
                />
                <button type="button" data-testid="auth-toggle-password" onClick={() => setShowPass((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <input data-testid="auth-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 rounded accent-emerald-600" />
                  Remember me
                </label>
                <button type="button" data-testid="auth-forgot-btn" onClick={handleForgot} className="font-semibold text-emerald-700 dark:text-emerald-400">Forgot Password?</button>
              </div>
            )}

            <button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-emerald-800 hover:bg-emerald-900 dark:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-emerald-950 font-bold py-3.5 rounded-2xl transition-colors disabled:opacity-60 shadow-lg shadow-emerald-800/20"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLogin ? "Login" : "Create Account"}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button type="button" data-testid={`auth-tab-${isLogin ? "register" : "login"}`} onClick={() => setMode(isLogin ? "register" : "login")} className="font-bold text-emerald-700 dark:text-emerald-400">
              {isLogin ? "Sign Up" : "Login"}
            </button>
          </p>
        </div>

        <button data-testid="login-guest-btn" onClick={handleGuest} className="mt-4 w-full text-sm font-semibold text-emerald-700 dark:text-emerald-400 py-2 hover:underline">
          Skip as Guest →
        </button>

        <p className="mt-auto pt-6 text-center text-[11px] text-slate-400 leading-relaxed">
          By continuing you agree to our Terms of Service & Privacy Policy.
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, icon: Icon, testid, ...props }) => (
  <div>
    <span className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ml-1">{label}</span>
    <div className="relative">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
    <input
      data-testid={testid}
      {...props}
      className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition"
    />
    </div>
  </div>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.22V7.04H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);
